import React, { useState, useRef, useEffect } from 'react';
import './PinLoginForm.css';
import { normalizeEmail } from '../utils/emailNormalization';

interface PinLoginFormProps {
  onSubmit: (email: string, pin: string) => Promise<void>;
  loading?: boolean;
  error?: string;
  onClearError?: () => void;
}

export const PinLoginForm: React.FC<PinLoginFormProps> = ({
  onSubmit,
  loading = false,
  error,
  onClearError
}) => {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [emailError, setEmailError] = useState('');
  const [pinError, setPinError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);
  
  // Refs for PIN input fields
  const pinRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null)
  ];

  // Clear errors when user starts typing
  useEffect(() => {
    if (error && onClearError) {
      onClearError();
    }
    setEmailError('');
    setPinError('');
  }, [email, pin, error, onClearError]);

  // Reset hasAttempted when PIN changes after an attempt
  useEffect(() => {
    if (hasAttempted) {
      setHasAttempted(false);
    }
  }, [pin]);

  // Auto-submit when PIN is complete (but only once per PIN entry)
  useEffect(() => {
    const isComplete = pin.every(digit => digit !== '');
    if (isComplete && email && !isSubmitting && !hasAttempted) {
      handleSubmit();
    }
  }, [pin, email, isSubmitting, hasAttempted]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('Email is required');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validatePin = (pinArray: string[]): boolean => {
    const isComplete = pinArray.every(digit => digit !== '');
    if (!isComplete) {
      setPinError('Please enter your complete 4-digit PIN');
      return false;
    }
    
    const hasInvalidChars = pinArray.some(digit => !/^[0-9]$/.test(digit));
    if (hasInvalidChars) {
      setPinError('PIN must contain only numbers (0-9)');
      return false;
    }
    
    setPinError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePinChange = (index: number, value: string) => {
    // Only allow single digits
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    // Only allow numbers
    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!pin[index] && index > 0) {
        // If current field is empty, move to previous field and clear it
        const newPin = [...pin];
        newPin[index - 1] = '';
        setPin(newPin);
        pinRefs[index - 1].current?.focus();
      } else if (pin[index]) {
        // Clear current field
        const newPin = [...pin];
        newPin[index] = '';
        setPin(newPin);
      }
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      pinRefs[index - 1].current?.focus();
    }
    if (e.key === 'ArrowRight' && index < 3) {
      pinRefs[index + 1].current?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 4);
        if (digits.length === 4) {
          const newPin = digits.split('');
          setPin(newPin);
          pinRefs[3].current?.focus();
        }
      });
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (isSubmitting || loading) {
      return;
    }

    const isEmailValid = validateEmail(email);
    const isPinValid = validatePin(pin);
    
    if (!isEmailValid || !isPinValid) {
      return;
    }

    setIsSubmitting(true);
    setHasAttempted(true);
    try {
      const pinString = pin.join('');
      // Normalize email to lowercase before submitting
      const normalizedEmail = normalizeEmail(email);
      await onSubmit(normalizedEmail, pinString);
    } catch (error) {
      // Error handling is done by parent component
      // Clear PIN on error to prevent auto-retry
      clearPin();
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearPin = () => {
    setPin(['', '', '', '']);
    pinRefs[0].current?.focus();
  };

  const isFormValid = email && pin.every(digit => digit !== '');

  return (
    <div className="pin-login-form">
      <div className="pin-login-header">
        <h2>Welcome to the Party!</h2>
        <p>Enter your email and 4-digit PIN to join the celebration</p>
        <p className="pin-info">
          <small>Your PIN is a 4-digit number (0000-9999) provided in your invitation email</small>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="pin-form">
        {/* Email Input */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            className={`form-input ${emailError ? 'error' : ''}`}
            placeholder="your.email@example.com"
            disabled={loading || isSubmitting}
            autoComplete="email"
          />
          {emailError && <div className="error-message">{emailError}</div>}
        </div>

        {/* PIN Input */}
        <div className="form-group">
          <label className="form-label">
            Your 4-Digit PIN
          </label>
          <div className="pin-input-container">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={pinRefs[index]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                className={`pin-digit ${pinError ? 'error' : ''}`}
                maxLength={1}
                disabled={loading || isSubmitting}
                autoComplete="off"
                aria-label={`PIN digit ${index + 1}`}
              />
            ))}
          </div>
          {pinError && <div className="error-message">{pinError}</div>}
          
          {/* PIN Display (dots for filled digits) */}
          <div className="pin-display">
            {pin.map((digit, index) => (
              <span key={index} className={`pin-dot ${digit ? 'filled' : ''}`}>
                {digit ? '•' : '○'}
              </span>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message global-error">
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div className="form-actions">
          <button
            type="button"
            onClick={clearPin}
            className="btn-secondary"
            disabled={loading || isSubmitting || pin.every(d => !d)}
          >
            Clear PIN
          </button>
          
          <button
            type="submit"
            className="btn-primary"
            disabled={!isFormValid || loading || isSubmitting}
          >
            {loading || isSubmitting ? (
              <>
                <span className="spinner"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </div>

        {/* Mobile Numeric Keypad */}
        <div className="mobile-keypad">
          <div className="keypad-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((num) => (
              <button
                key={num}
                type="button"
                className="keypad-btn"
                onClick={() => {
                  const emptyIndex = pin.findIndex(digit => digit === '');
                  if (emptyIndex !== -1) {
                    handlePinChange(emptyIndex, num.toString());
                  }
                }}
                disabled={loading || isSubmitting || pin.every(d => d !== '')}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              className="keypad-btn keypad-backspace"
              onClick={() => {
                const lastFilledIndex = pin.map((d, i) => d ? i : -1).filter(i => i !== -1).pop();
                if (lastFilledIndex !== undefined) {
                  const newPin = [...pin];
                  newPin[lastFilledIndex] = '';
                  setPin(newPin);
                  pinRefs[lastFilledIndex].current?.focus();
                }
              }}
              disabled={loading || isSubmitting || pin.every(d => !d)}
            >
              ⌫
            </button>
          </div>
        </div>

        {/* Help Text */}
        <div className="help-text">
          <p>
            <strong>Need help?</strong> Your 4-digit PIN was provided when you were invited to the party.
          </p>
          <p>
            • Check your welcome email for your PIN<br/>
            • Contact the party organizer if you can't find it<br/>
            • Make sure you're using the correct email address
          </p>
          <p>
            <small>
              <strong>Security:</strong> Your account will be temporarily locked after 5 failed attempts.
            </small>
          </p>
        </div>
      </form>
    </div>
  );
};

export default PinLoginForm;